import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { unlinkBook, type Book } from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import { aroundAmount } from "./money";
import { useTranslation } from "./TranslationBanner";

/**
 * LinkedBanner is a linked book's own strip on its studio page (requirement
 * 0038, ADR-0093): what it is derived from, and the way out.
 *
 * **It says what the book is, not what a refresh would do** (ADR-0096's rule
 * about panels, applied to itself). Refresh, its preview and its warning live on
 * the book's settings screen beside the instruction that configures them
 * (ADR-0096 *Since*, requirement 0042) — this screen is a list of chapters, and
 * a control that re-translates the whole book is not a chapter.
 *
 * That is also why it no longer fetches the refresh preview: the comparison is
 * read by the one screen that offers the press, rather than once per view of the
 * screen an author opens most.
 *
 * Nothing for a book that is not linked.
 */
export default function LinkedBanner({ book }: { book: Book }) {
  const t = useT();
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  // The run carries the source's title, so the strip names the book rather than
  // its id without a second fetch for it.
  const run = useTranslation(book.id);

  const unlink = useMutation({
    mutationFn: () => unlinkBook(book.id),
    // Invalidated by prefix, not written to one key: the studio reads the
    // working copy under ["book", id, "draft"] and the reader's page under
    // ["book", id], and unlinking changes what both of them may do. Setting the
    // one key left the studio showing this strip over a book that was already
    // unlinked.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["book", book.id] });
      void client.invalidateQueries({ queryKey: ["books"] });
    },
  });

  if (!book.linked_source_id) return null;

  return (
    <section
      data-testid="linked-banner"
      className="mb-6 rounded-card border border-line bg-sunk p-3 text-sm"
    >
      <p className="font-medium">
        <Link to={`/studio/books/${book.linked_source_id}`} className="hover:text-sage-deep">
          {/* The source title is authored text inside a sentence, so it keeps
              its own direction whatever script the interface is in. */}
          {aroundTitle(t.translation.linkedTo, run.data?.source_title ?? "")}
        </Link>
      </p>
      <p className="mt-1 text-xs text-muted">{t.translation.linkedBlurb}</p>

      {/* What the book **is**: a count of unreviewed items is state, not a
          diff, so it belongs on the banner and not beside the Refresh press
          (ADR-0096's rule about panels, ADR-0098 §8). */}
      {!!run.data?.open_conflicts && (
        <p className="mt-2">
          <Link
            to={`/studio/books/${book.id}/conflicts`}
            data-testid="linked-conflicts"
            className="inline-block rounded-full border border-sage bg-sage-soft px-3 py-1 text-xs font-medium text-sage-deep hover:bg-sage-soft/70"
          >
            {t.translation.conflictsBadge(run.data.open_conflicts)}
          </Link>
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {confirming ? (
          <>
            <span className="self-center text-xs">{t.translation.unlinkQuestion}</span>
            <button
              type="button"
              data-testid="linked-unlink-confirm"
              disabled={unlink.isPending}
              onClick={() => unlink.mutate(undefined, { onSettled: () => setConfirming(false) })}
              className="rounded-full border border-red-300 px-4 py-2 text-sm text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
            >
              {t.translation.unlink}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-full border border-line px-4 py-2 text-sm hover:bg-surface"
            >
              {t.common.keep}
            </button>
          </>
        ) : (
          <button
            type="button"
            data-testid="linked-unlink"
            onClick={() => setConfirming(true)}
            className="rounded-full border border-line px-4 py-2 text-sm text-ink-soft transition-colors hover:bg-surface"
          >
            {t.translation.unlink}
          </button>
        )}
      </div>

      <MutationError
        className="mt-2"
        testId="linked-unlink-error"
        error={unlink.error}
        fallback={t.translation.unlinkFailed}
      />
    </section>
  );
}

/** aroundTitle puts an authored title inside a sentence, isolated so its script keeps its own direction. */
function aroundTitle(sentence: (title: string) => string, title: string) {
  const [before, after] = aroundAmount(sentence);
  return (
    <>
      {before}
      <bdi>{title}</bdi>
      {after}
    </>
  );
}
